import { useFacebookSdk } from '@hooks/useFacebookSdk'
import { WhatsappBotWABAWithConfiguration } from '@models/WhatsappBot'
import { registerVenueWhatsappBotDetailsBySlug } from '@services/VenueService'
import { useAppSelector } from '@store/hooks'
import { selectSelectedVenue } from '@store/VenueReducer'
import { HELP_CENTER_WHATSAPP_CONNECTION_URL } from '@utils/urls'
import { Button, Flex } from 'antd'
import { useEffect, useRef, useState } from 'react'

/**
 * WhatsappEmbeddedSignup component.
 *
 * This component is responsible for managing the WhatsApp embedded sign-up process for a selected venue.
 * It fetches and handles the configuration details for the WhatsApp bot, including the acquisition
 * of the WhatsApp Business Account (WABA) ID and associated phone number ID through event listeners.
 * The component leverages asynchronous hooks for data retrieval, state management, and conditional
 * rendering, and integrates with the Facebook SDK to obtain authorization codes required for registration.
 *
 * Technical details: This implementation provides meticulous configuration details by orchestrating
 * asynchronous event listeners and API calls to dynamically configure and update the WhatsApp bot setup,
 * ensuring robust data handling and state synchronization via React hooks, refs, and error monitoring.
 */
interface WhatsappEmbeddedSignupProps {
  whatsappBotDetails?: WhatsappBotWABAWithConfiguration
  registerSuccessCallback?: () => void
}

const WhatsappEmbeddedSignup: React.FC<WhatsappEmbeddedSignupProps> = ({
  whatsappBotDetails,
  registerSuccessCallback,
}) => {
  const venue = useAppSelector(selectSelectedVenue())
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { fbSdkReady } = useFacebookSdk(whatsappBotDetails?.appId || undefined)
  const wabaIdRef = useRef<string | undefined>(undefined)
  const phoneNumberIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      )
        return
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          // console.log('message event: ', data)
          wabaIdRef.current = data.data?.waba_id
          phoneNumberIdRef.current = data.data?.phone_number_id
        }
      } catch {
        console.error('message event: ', event.data)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  const fbLoginCallback = (response: any) => {
    if (response.authResponse) {
      const code = response.authResponse.code

      const handleApiCall = async () => {
        if (!venue) {
          console.error('Venue not found')
          return
        }

        try {
          setIsLoading(true)
          await registerVenueWhatsappBotDetailsBySlug(venue.slug, {
            code,
            wabaId: wabaIdRef.current,
            phoneNumberId: phoneNumberIdRef.current,
          })
        } catch (error) {
          console.error('API Error:', error)
        }
        setIsLoading(false)

        registerSuccessCallback && registerSuccessCallback()
      }

      handleApiCall()
    } else {
      console.error('Login failed: ', response)
      if (response.errors) {
        console.error('API Errors: ', response.errors)
      }
    }
  }

  const launchWhatsAppSignup = () => {
    if (!window.FB) {
      console.error('Facebook SDK not loaded')
      return
    }

    FB.login(fbLoginCallback, {
      config_id: whatsappBotDetails?.configurationId,
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        setup: {},
        featureType: '',
        sessionInfoVersion: '3',
      },
    })
  }

  return (
    <>
      {!whatsappBotDetails?.waba && (
        <Flex align="center">
          {!whatsappBotDetails?.waba && (
            <Button
              type="primary"
              onClick={launchWhatsAppSignup}
              disabled={!fbSdkReady}
              loading={isLoading}
            >
              Connect WhatsApp
            </Button>
          )}
          <Button
            type="link"
            target="_blank"
            href={HELP_CENTER_WHATSAPP_CONNECTION_URL}
            style={{ textDecoration: 'underline' }}
          >
            Manual installation
          </Button>
        </Flex>
      )}
    </>
  )
}

type WhatsappEmbeddedSignupType = {
  WhatsappEmbeddedSignup: typeof WhatsappEmbeddedSignup
}

export { WhatsappEmbeddedSignup, type WhatsappEmbeddedSignupType }
